import type { SocialListeningRun } from "src/services/SocialListeningAPIs";
import type { BrandListeningReportData, VoiceQuote } from "../Types/ListeningReportTypes";

export const MOCK_REPORT_ROUTE = "/account/social-listing-agent/listening-report";

/** The sample report row; `run_id` 0 never collides with a real run. */
export const MOCK_HISTORY_RUN: SocialListeningRun = {
  run_id: 0,
  analysis_type: "competitive",
  analyses_by: "Competitive Analysis",
  created_by: { user_id: 0, name: "DataTram" },
  status: "completed",
  company_name: "ETHNC",
  competitors: ["Khaadi"],
  request_id: "2sqmShJ9hE2",
  data_quality_score: null,
  sources_analyzed: null,
  total_count: null,
  positive_percentage: null,
  neutral_percentage: null,
  negative_percentage: null,
  created_at: "2026-08-03T03:53:00Z",
  completed_at: "2026-08-03T03:53:00Z",
};

const quotes = (items: [string, string][]): VoiceQuote[] =>
  items.map(([text, topic]) => ({ text, topic }));

export const BRAND_LISTENING_MOCK_REPORT: BrandListeningReportData = {
  title: "ETHNC Brand Listening Report",
  primaryBrand: { name: "ETHNC", scoredItems: 179, sources: ["Instagram", "Reddit"] },
  competitor: { name: "Khaadi", scoredItems: 72, sources: ["Reddit"] },
  requestId: "2sqmShJ9hE2",
  generatedOn: "3 Aug 2026",
  meta: [
    { label: "Request ID", value: "2sqmShJ9hE2" },
    { label: "Run timestamp", value: "2026-08-03 03:53" },
    { label: "Available ETHNC sources", value: "Instagram, Reddit" },
    { label: "Data window", value: "Last 3 months" },
    { label: "Generated by", value: "[User name]" },
  ],
  limitedDataNotice: {
    title: "This report was compiled on limited data, so metrics may be skewed.",
    body: "ETHNC: 2 posts (1 Instagram, 1 Reddit). Khaadi: 1 Reddit thread. Facebook, Facebook groups and TikTok returned no data for either brand. Read the findings as directional.",
  },
  kpis: [
    {
      id: "items",
      label: "ETHNC Items Scored",
      help: "Posts and comments that were scored for sentiment",
      icon: "messages",
      value: "179",
      detail: "2 posts",
      compare: "plus audience comments",
    },
    {
      id: "positive",
      label: "ETHNC Positive",
      help: "Share of scored items with positive sentiment",
      icon: "positive",
      value: "57.5%",
      trend: { tone: "pos", direction: "up" },
      detail: "103 positive items",
      compare: "vs Khaadi 62.5%",
    },
    {
      id: "negative",
      label: "ETHNC Negative",
      help: "Share of scored items with negative sentiment",
      icon: "negative",
      value: "21.8%",
      trend: { tone: "neg", direction: "down" },
      detail: "39 negative items",
      compare: "vs Khaadi 9.7%",
    },
    {
      id: "risk",
      label: "Top Risk Theme",
      help: "Theme with the most negative mentions",
      icon: "risk",
      value: "Price",
      trend: { tone: "neg", direction: "down" },
      detail: "12 mentions",
      compare: "negative price/value",
    },
  ],
  readout: [
    [
      { text: "ETHNC’s on-ground reality is " },
      { text: "mixed rather than simply positive or negative", tone: "strong" },
      { text: ". Customers respond warmly to the visual side of the brand: " },
      {
        text: "colors, dresses, styling, accessories, and outfit inspiration are repeatedly praised",
        tone: "pos",
      },
      {
        text: ". At the same time, the strongest complaint cluster is commercial and product-value related: ",
      },
      {
        text: "prices, expensive international stores, perceived quality decline, old designs, and fitting concerns",
        tone: "neg",
      },
      { text: "." },
    ],
    [
      { text: "The key business implication is that " },
      {
        text: "ETHNC does not have a visibility problem in this sample; it has a value-confidence problem",
        tone: "mark",
      },
      { text: ". People like the look of the clothes, but some customers question whether " },
      { text: "the price, quality, and design freshness justify the purchase", tone: "strong" },
      { text: "." },
    ],
    [
      { text: "Compared with Khaadi, " },
      { text: "ETHNC has a higher negative share in this run", tone: "strong" },
      { text: ": " },
      { text: "21.8%", tone: "neg" },
      { text: " negative versus Khaadi’s " },
      { text: "9.7%", tone: "pos" },
      {
        text: ". Khaadi’s sample is narrower, but the available comments are more consistently positive around accessories and styling, while ETHNC has a ",
      },
      {
        text: "sharper contrast between praise for aesthetics and criticism of price/quality",
        tone: "strong",
      },
      { text: "." },
    ],
  ],
  competitiveSentiment: {
    bars: [
      {
        name: "ETHNC",
        items: 179,
        share: { positive: 57.5, neutral: 20.7, negative: 21.8 },
      },
      {
        name: "Khaadi",
        items: 72,
        share: { positive: 62.5, neutral: 27.8, negative: 9.7 },
        flag: "Small sample · 1 source",
      },
    ],
    note: {
      lead: "Khaadi leads on sentiment:",
      body: "it has the higher positive share (62.5% vs 57.5%) and lower negative share (9.7% vs 21.8%) in this run, though its data comes from a single Reddit source.",
    },
  },
  platformSentiment: {
    bars: [
      {
        name: "Instagram",
        items: 45,
        share: { positive: 31.1, neutral: 17.8, negative: 51.1 },
      },
      {
        name: "Reddit",
        items: 134,
        share: { positive: 66.4, neutral: 21.6, negative: 11.9 },
      },
    ],
    note: {
      lead: "Instagram is the pressure point:",
      body: "23 of 45 comments were negative. Reddit is net-positive, with 89 of 134 items positive.",
    },
  },
  sourceCoverageNote:
    "Every source the agent checked for each brand, including those that returned nothing. ETHNC’s overall score leans on Reddit volume; its most negative signal comes from Instagram.",
  sourceCoverage: [
    { brand: "ETHNC", source: "Instagram", collected: true, posts: 1, comments: 45, scoredItems: 45, scoredPercent: 33.6 },
    { brand: "ETHNC", source: "Reddit", collected: true, posts: 1, comments: 132, scoredItems: 134, scoredPercent: 100 },
    { brand: "ETHNC", source: "Facebook", collected: false, posts: 0, comments: 0 },
    { brand: "ETHNC", source: "Facebook Group", collected: false, posts: 0, comments: 0 },
    { brand: "ETHNC", source: "TikTok", collected: false, posts: 0, comments: 0 },
    { brand: "Khaadi", source: "Reddit", collected: true, posts: 1, comments: 70, scoredItems: 72, scoredPercent: 53.7 },
    { brand: "Khaadi", source: "Instagram", collected: false, posts: 0, comments: 0 },
    { brand: "Khaadi", source: "Facebook", collected: false, posts: 0, comments: 0 },
    { brand: "Khaadi", source: "Facebook Group", collected: false, posts: 0, comments: 0 },
    { brand: "Khaadi", source: "TikTok", collected: false, posts: 0, comments: 0 },
  ],
  engagementBySource: [
    { brand: "ETHNC", source: "Instagram", posts: 1, likes: 1240, comments: 45, shares: 18, averageEngagement: 1303 },
    { brand: "ETHNC", source: "Reddit", posts: 1, likes: 310, comments: 132, shares: null, averageEngagement: 442 },
    { brand: "Khaadi", source: "Reddit", posts: 1, likes: 185, comments: 70, shares: null, averageEngagement: 255 },
  ],
  topPosts: [
    {
      title: "ETHNC Instagram post",
      brand: "ETHNC",
      source: "Instagram",
      likes: 1240,
      comments: 45,
      shares: 18,
      sentiment: { label: "Mostly negative", tone: "neg" },
    },
    {
      title: "ETHNC Reddit thread",
      brand: "ETHNC",
      source: "Reddit",
      likes: 310,
      comments: 132,
      shares: null,
      sentiment: { label: "Mostly positive", tone: "pos" },
    },
    {
      title: "Khaadi Reddit thread",
      brand: "Khaadi",
      source: "Reddit",
      likes: 185,
      comments: 70,
      shares: null,
      sentiment: { label: "Mostly positive", tone: "pos" },
    },
  ],
  drivers: [
    { name: "Styling / aesthetics", positive: 35, neutral: 6, negative: 6, note: "The strongest positive equity is how the product looks." },
    { name: "Fit / sizing", positive: 8, neutral: 3, negative: 6, note: "Polarized: outfit posts drive praise, but sizing and cut concerns remain." },
    { name: "Price / value", positive: 0, neutral: 3, negative: 12, note: "The clearest source of friction." },
    { name: "Quality / fabric", positive: 1, neutral: 2, negative: 7, note: "Quality concerns weaken value perception." },
    { name: "Design / variety", positive: 1, neutral: 0, negative: 6, note: "Some customers see designs as old or less fresh." },
    { name: "Safety / DMs", positive: 1, neutral: 3, negative: 4, note: "Not a product issue, but it affects the social-platform experience." },
  ],
  driverInsights: [
    {
      title: "What Customers Push Back On",
      tag: "Needs fixing",
      tagTone: "neg",
      body: "It’s not only “expensive”. Customers link price to quality, old designs and overseas-store value, which makes this bigger than a discounting problem.",
    },
    {
      title: "What Customers Love",
      tag: "Keep doing",
      tagTone: "pos",
      body: "ETHNC still has strong aesthetic pull. Dress, purple, pretty, love, colour and stunning show styling is a real brand strength.",
    },
    {
      title: "What To Act On",
      tag: "Look into it",
      body: "Protect the styling strength while closing trust gaps: clearer price communication, proof of quality, signals of new designs and fit reassurance.",
    },
  ],
  themeComparison: [
    {
      theme: "Styling / aesthetics",
      primary: { net: 16.2, praise: 35, complaints: 6, mentions: 41 },
      competitor: { net: 19.4, praise: 16, complaints: 2, mentions: 18 },
      leader: "Khaadi",
      lowSample: false,
    },
    {
      theme: "Price / value",
      primary: { net: -6.7, praise: 0, complaints: 12, mentions: 12 },
      competitor: { net: -2.8, praise: 1, complaints: 3, mentions: 4 },
      leader: "Khaadi",
      lowSample: true,
    },
    {
      theme: "Quality / fabric",
      primary: { net: -3.4, praise: 1, complaints: 7, mentions: 8 },
      competitor: { net: 0, praise: 1, complaints: 1, mentions: 2 },
      leader: "Khaadi",
      lowSample: true,
    },
    {
      theme: "Design / variety",
      primary: { net: -2.8, praise: 1, complaints: 6, mentions: 7 },
      competitor: { net: 4.2, praise: 4, complaints: 1, mentions: 5 },
      leader: "Khaadi",
      lowSample: true,
    },
    {
      theme: "Fit / sizing",
      primary: { net: 1.1, praise: 8, complaints: 6, mentions: 14 },
      competitor: { net: 0, praise: 1, complaints: 1, mentions: 2 },
      leader: "Too close",
      lowSample: true,
    },
  ],
  wins: [
    {
      lead: "Styling is the biggest theme in the run.",
      body: "35 positive mentions, more than all price, quality, design and safety complaints combined (29).",
    },
    {
      lead: "Reddit is warmer for ETHNC than Khaadi overall.",
      body: "66.4% of ETHNC’s Reddit items are positive (89 of 134), against Khaadi’s 62.5% (45 of 72). Khaadi’s sample is small, so treat this gap as directional.",
    },
    {
      lead: "Fit is net-positive.",
      body: "Outfit posts earn more praise (8) than complaints (6).",
    },
  ],
  voices: [
    {
      id: "price",
      title: "ETHNC: Price and Value",
      countLabel: "12 negative mentions",
      badge: { label: "Risk", tone: "neg" },
      sentiment: { label: "Negative", tone: "neg" },
      total: 12,
      quotes: quotes([
        ["Why the outlets open their branches abroad, their price hike is quite ridiculous.", "Overseas stores"],
        ["Very expensive old designs and compared to Pakistani prices multiple with 3.", "Price gap"],
        ["Hell expensive.", "Price"],
        ["Same kurta is almost half the price in Lahore.", "Price gap"],
        ["Love the designs but can’t justify these prices anymore.", "Price"],
        ["Sale prices here are still more than full price back home.", "Price gap"],
        ["Too pricey for lawn, honestly.", "Price"],
        ["Why is shipping more than the shirt itself?", "Delivery cost"],
        ["Waited for the sale and it’s still too expensive.", "Price"],
        ["The overseas outlet markup is crazy.", "Overseas stores"],
      ]),
    },
    {
      id: "quality",
      title: "ETHNC: Quality, Design and Fit",
      countLabel: "19 negative mentions",
      badge: { label: "Risk", tone: "neg" },
      sentiment: { label: "Negative", tone: "neg" },
      total: 19,
      quotes: quotes([
        ["I was there twice: prices sky high and service awful and quality poor.", "Quality"],
        ["Still more variety and new designs in Pakistan. Here they sell rejected from Pakistan.", "Design"],
        ["Ethnic clothes fitting is terrible. All of their clothes have a weird under arm cut.", "Fit"],
        ["Colour faded after the second wash.", "Quality"],
        ["Stitching came loose within a month.", "Quality"],
        ["Same prints as last season, nothing new.", "Design"],
        ["Sizes run small, had to exchange twice.", "Fit"],
        ["Fabric feels thinner than it used to.", "Quality"],
        ["The cut isn’t flattering for taller women.", "Fit"],
        ["Store staff didn’t help at all.", "Service"],
      ]),
    },
    {
      id: "styling",
      title: "ETHNC: Styling",
      countLabel: "35 positive mentions",
      badge: { label: "Strength", tone: "pos" },
      sentiment: { label: "Positive", tone: "pos" },
      total: 35,
      quotes: quotes([
        ["Such a pretty colour, looks really elegant.", "Colour"],
        ["10/10, suit is suiting you, looking stunning.", "Outfit"],
        ["Wow that dress is awesome, you look lovely.", "Dress"],
        ["The purple one is gorgeous!", "Colour"],
        ["Obsessed with this colour combination.", "Colour"],
        ["Such an elegant outfit, love it.", "Outfit"],
        ["The embroidery on this is beautiful.", "Design"],
        ["Perfect for Eid, looks amazing.", "Outfit"],
        ["The dupatta styling is so pretty.", "Accessories"],
        ["Stunning look, where is this from?", "Outfit"],
      ]),
    },
  ],
  words: [
    { word: "good", weight: 72, sentiment: "pos" },
    { word: "dress", weight: 69, sentiment: "pos" },
    { word: "purple", weight: 69, sentiment: "pos" },
    { word: "pretty", weight: 58, sentiment: "pos" },
    { word: "love", weight: 55, sentiment: "pos" },
    { word: "looks", weight: 55, sentiment: "pos" },
    { word: "prices", weight: 46, sentiment: "neg" },
    { word: "like", weight: 43, sentiment: "pos" },
    { word: "one", weight: 43, sentiment: "neg" },
    { word: "well", weight: 43, sentiment: "pos" },
    { word: "look", weight: 43, sentiment: "pos" },
    { word: "suit", weight: 43, sentiment: "neu" },
    { word: "color", weight: 43, sentiment: "pos" },
    { word: "looking", weight: 39, sentiment: "pos" },
    { word: "post", weight: 39, sentiment: "pos" },
    { word: "even", weight: 39, sentiment: "pos" },
    { word: "price", weight: 36, sentiment: "neg" },
    { word: "clothes", weight: 36, sentiment: "neg" },
    { word: "better", weight: 36, sentiment: "neg" },
    { word: "youu", weight: 36, sentiment: "pos" },
    { word: "suits", weight: 32, sentiment: "pos" },
    { word: "amazing", weight: 32, sentiment: "pos" },
    { word: "happy", weight: 32, sentiment: "pos" },
    { word: "design", weight: 32, sentiment: "neg" },
    { word: "koi", weight: 32, sentiment: "neg" },
    { word: "high", weight: 32, sentiment: "neg" },
    { word: "itny", weight: 32, sentiment: "neg" },
    { word: "see", weight: 32, sentiment: "neu" },
    { word: "got", weight: 32, sentiment: "neu" },
    { word: "room", weight: 32, sentiment: "pos" },
    { word: "dms", weight: 32, sentiment: "neg" },
    { word: "beautiful", weight: 32, sentiment: "pos" },
    { word: "expensive", weight: 29, sentiment: "neg" },
    { word: "pakistan", weight: 29, sentiment: "neg" },
    { word: "old", weight: 29, sentiment: "neg" },
    { word: "quality", weight: 29, sentiment: "neg" },
    { word: "fit", weight: 29, sentiment: "pos" },
    { word: "stunning", weight: 29, sentiment: "pos" },
    { word: "safe", weight: 29, sentiment: "neg" },
    { word: "creeps", weight: 29, sentiment: "neg" },
  ],
  nextSteps: [
    {
      priority: "Priority 1",
      effort: { label: "High effort", level: "hi" },
      title: "Repair Value Confidence",
      body: "Treat price, quality and design freshness as one connected perception issue. Defending price alone won’t resolve the complaint unless customers also see credible quality and newness.",
      tags: ["Price clarity", "Quality proof", "New designs"],
    },
    {
      priority: "Priority 2",
      effort: { label: "Low effort", level: "lo" },
      title: "Keep Aesthetics Central",
      body: "The praise is specific: colour, dresses, outfits, accessories and styling. Keep these at the core of content and product storytelling.",
      tags: ["Styled content", "Colour stories", "Fit guidance"],
    },
    {
      priority: "Priority 3",
      effort: { label: "Medium effort", level: "md" },
      title: "Benchmark Against Khaadi",
      body: "Khaadi’s sampled sentiment is cleaner, with less negativity and stronger accessory and styling associations. ETHNC can compete by reducing the price and quality friction Khaadi doesn’t show as strongly.",
      tags: ["Negative share", "Add Facebook data"],
    },
  ],
};
